import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPalletDrafts } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileEdit, Play, Plus, User as UserIcon } from 'lucide-react';

/**
 * Draft pallets card — shows all in-progress (status=packing) pallets so users can resume.
 * Props:
 *   - compact: when true, hides footer actions (used on Pack page).
 *   - onlyMine: when true, only show current user's drafts.
 *   - onEmpty: optional callback — called when the drafts list is empty.
 */
export default function DraftPalletsCard({ compact = false, onlyMine = false, onEmpty }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    load();
  }, [onlyMine]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const res = await getPalletDrafts(onlyMine);
      const list = Array.isArray(res.data) ? res.data : [];
      setDrafts(list);
      if (list.length === 0 && onEmpty) onEmpty();
    } catch {
      setDrafts([]);
    }
    setLoading(false);
  };

  const formatAge = (minutes) => {
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const h = Math.floor(minutes / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  if (loading || drafts.length === 0) return null;

  return (
    <Card className="border-0 shadow-sm border-l-4 border-l-amber-400" data-testid="draft-pallets-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileEdit size={16} className="text-amber-600" />
          {onlyMine ? 'Your Drafts' : 'In-Progress Pallets'}
          <Badge variant="outline" className="ml-1 text-[10px]">{drafts.length}</Badge>
          <span className="ml-auto text-[10px] font-normal text-gray-400">
            tap any row to continue packing
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {drafts.slice(0, compact ? 3 : 6).map((d) => (
          <button
            key={d.id}
            onClick={() => navigate(`/pack/${d.id}`)}
            className="w-full flex items-center gap-3 p-2 rounded-lg border hover:border-[#1a2fce] hover:bg-[#1a2fce]/5 transition-colors text-left"
            data-testid={`resume-draft-${d.pallet_ref}`}
          >
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
              <FileEdit size={16} className="text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-sm">{d.pallet_ref}</span>
                {d.is_mine && <Badge variant="outline" className="text-[9px] py-0 h-4">yours</Badge>}
              </div>
              <div className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
                <span>{d.items_count} item{d.items_count !== 1 ? 's' : ''}</span>
                <span>·</span>
                <span>{d.boxes_count} box{d.boxes_count !== 1 ? 'es' : ''}</span>
                <span>·</span>
                <span>{formatAge(d.minutes_open)}</span>
              </div>
              {!d.is_mine && (
                <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                  <UserIcon size={10} /> started by {d.created_by_name}
                </div>
              )}
            </div>
            <Play size={16} className="text-[#1a2fce] shrink-0" />
          </button>
        ))}
        {drafts.length > (compact ? 3 : 6) && (
          <div className="text-[10px] text-gray-400 text-center pt-1">
            Showing {compact ? 3 : 6} of {drafts.length}
          </div>
        )}
        {!compact && (
          <Button
            onClick={() => navigate('/pack')}
            variant="outline"
            className="w-full h-9 text-xs mt-1"
            data-testid="start-new-pack"
          >
            <Plus size={12} className="mr-1" /> Start a New Pallet
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
